export type Nullable<T> = T | null

export type Undefinable<T> = T | undefined

export type EmptyObject = {
  [K in string | number]: never
}

export type PartialRecord<K extends keyof any, T> = {
  [P in K]?: T
}

export type ApplicationProgramCode = 'DAMU' | 'OWN_PROGRAM'

export type Language = 'kk' | 'ru'

export type ApplicationFlowTypes =
  | 'MAIN_FLOW'
  | 'RESCORING'
  | 'DAMU'
  | 'DAMU_WITH_INITIAL_FEE'
  | 'OWN'
  | 'OWN_AUTO'
  | 'IMPROVE_CREDIT_CONDITIONS'
  | 'IMPROVE_PLEDGE_CREDIT_CONDITIONS'
  | 'COLLATERAL'
  | 'CONFIRMATION_SPOUSE'
  | 'APPRAISER'
  | 'NOTARY'
  | 'CONFIRMATION_THIRD_PARTY_PLEDGE'
  | 'THIRD_PARTY_PLEDGE'
  // Test flows
  | 'DEV_1'
  | 'DEV_2'
  | 'DEV_3'
  | 'MAIN_FLOW_IBUL'

export type ImproveFlows =
  | 'IMPROVE_CREDIT_CONDITIONS'
  | 'IMPROVE_PLEDGE_CREDIT_CONDITIONS'
  | 'CONFIRMATION_THIRD_PARTY_PLEDGE'

export type ApplicationCreditStatuses = 'NEW' | 'IN_PROGRESS' | 'FAILED' | 'PRE_ISSUED' | 'ISSUED' | 'CONFIRMED'

export type ServerNotification = {
  key: string
  type: 'server_notification'
  value: string
}

export type StepNotification = {
  key: string
  type: 'step_status'
  value: {
    count_attempts: number
    flow_id: string
    step_id: string
    is_internal: boolean
    is_trigger: boolean
    is_final: boolean
    is_success: boolean | null
    data: unknown
    step: string
    step_type: ApplicationSteps
    can_retry: boolean
    colvir_available_at: string
    depends_on_colvir_worktime: boolean
    is_colvir_worktime: boolean
    next_step_depends_on_colvir_worktime: boolean
  }
}

export type CommandProcessingResult = {
  key: string
  type: 'command_processing_result'
  value: string
}

export type ThirdPartyNotification = {
  key: string
  type: 'third_party_notifier'
  value: {
    flow_id: string
    flow_status: 'REJECTED' | 'DONE'
  }
}

export type SocketNotifications =
  | ServerNotification
  | StepNotification
  | CommandProcessingResult
  | ThirdPartyNotification

export type GetWsStepStatus = 'DONE' | 'REJECTED' | 'RETRY' | 'IN_PROGRESS' | 'STEP_NOT_STARTED' | undefined

export type GetWsStepStatuses = 'DONE' | 'FAILED' | 'IN_PROGRESS' | 'STEPS_NOT_STARTED' | undefined

export type ApplicationSteps = keyof typeof FLOW_STEPS

export type CurrentStepStatuses = 'IN_PROGRESS' | 'DONE' | 'FAILED' | 'REJECTED'

export type CurrentStepFlowStatuses =
  | 'NEW'
  | 'RETRY'
  | 'RETRY_WITHOUT_REDIRECT'
  | 'HOLD'
  | 'DONE'
  | 'REJECTED'
  | 'ISSUED'

export enum FLOW_STEPS {
  PRE_SCORE = 'PRE_SCORE', // 'Процесс предварительного скоринга'
  FETCH_PERSONAL_INFO = 'FETCH_PERSONAL_INFO', // 'Процесс получения данных заемщика'
  FETCH_THIRD_PARTY_PLEDGER_PERSONAL_INFO = 'FETCH_THIRD_PARTY_PLEDGER_PERSONAL_INFO', // 'Процесс получения данных залогодателя',
  BIOMETRY_AITU = 'BIOMETRY_AITU', // 'Процесс биометрии (aitu)'
  SIGN_DOCS_AITU = 'SIGN_DOCS_AITU', // 'Процесс подписания документов DID(aitu)'
  SIGN_DOCS_AITU_DAMU = 'SIGN_DOCS_AITU_DAMU', // 'Процесс подписания документов DID(aitu) для DAMU'
  SIGN_DOCS_AITU_PLEDGE = 'SIGN_DOCS_AITU_PLEDGE', // 'Процесс подписания документов DID(aitu) для Залоговой программы',
  SIGN_PDF_BANK_LOAN_AGREEMENT_AITU = 'SIGN_PDF_BANK_LOAN_AGREEMENT_AITU', // 'Процесс подписания ДБЗ DID(aitu) (PDF версия подписи)',
  SIGN_REPAYMENT_SCHEDULE_WITH_AITU = 'SIGN_REPAYMENT_SCHEDULE_WITH_AITU', // 'Процесс подписания графика погашения DID(aitu)',
  SIGN_BANK_LOAN_AGREEMENT_BY_BANK = 'SIGN_BANK_LOAN_AGREEMENT_BY_BANK', // 'Процесс подписания ДБЗ банком',
  SIGN_PLEDGE_AGREEMENT_BY_BANK = 'SIGN_PLEDGE_AGREEMENT_BY_BANK', // 'Процесс подписания ДЗ банком',
  SIGN_REPAYMENT_SCHEDULE_BY_BANK = 'SIGN_REPAYMENT_SCHEDULE_BY_BANK', // 'Процесс подписания Графика погашения банком',
  BIOMETRY_FORENSICS = 'BIOMETRY_FORENSICS', // 'Процесс биометрии (forensics)'
  GENERATE_DOCUMENTS_SIGNED_BY_OTP = 'GENERATE_DOCUMENTS_SIGNED_BY_OTP', // 'Генерация документов, подписанных OTP'
  GENERATE_THIRD_PARTY_PLEDGER_DOCUMENTS_SIGNED_BY_OTP = 'GENERATE_THIRD_PARTY_PLEDGER_DOCUMENTS_SIGNED_BY_OTP', // 'Генерация документов залогодателя, подписанных OTP',
  BORROWER_SCORE = 'BORROWER_SCORE', // 'Скоринг заемщика (score 1.2)'
  FETCH_DDOCS = 'FETCH_DDOCS', // 'Получение УДЛ из сервиса DigitalDocuments'
  CHECK_TAX_DEBT = 'CHECK_TAX_DEBT', // 'Получение наличия налоговой задолжностей'
  FETCH_PKB_REPORT = 'FETCH_PKB_REPORT', // 'Получение кредитного отчета ПКБ - заемщика'
  FETCH_THIRD_PARTY_PLEDGER_PKB_REPORT = 'FETCH_THIRD_PARTY_PLEDGER_PKB_REPORT', // 'Получение кредитного отчета ПКБ - залогодателя',
  INSURANCE_PLEDGE = 'INSURANCE_PLEDGE', // 'Получение страхового полиса на недвижимость'
  SCORING = 'SCORING', // 'Скоринг клиента'
  CHECK_DECISION_IIN = 'CHECK_DECISION_IIN', // 'Проверка лояльных ИИНов'
  PREVIOUS_SCORING = 'PREVIOUS_SCORING', // 'Вернуть предыдущие решение'
  DECISION = 'DECISION', // 'Решение клиента'
  SET_PAY_DAY = 'SET_PAY_DAY', // 'Выбор дня погашения'
  PAYBOX_PLEDGE_FEE_PAYMENT = 'PAYBOX_PLEDGE_FEE_PAYMENT', // 'Оплата гарантийного взноса через PayBox'
  SEND_CERT_CENTER_SIGNATURES = 'SEND_CERT_CENTER_SIGNATURES', // 'Пожалуйста подождите'
  PAYBOX_COMISSION_FEE_PAYMENT = 'PAYBOX_COMISSION_FEE_PAYMENT', // 'Оплата комиссии через PayBox'
  REGISTER_IN_COLVIR = 'REGISTER_IN_COLVIR', // 'Регистрация кредита в системе Colvir'
  REGISTER_IN_COLVIR_DAMU_PROGRAM = 'REGISTER_IN_COLVIR_DAMU_PROGRAM', // 'Регистрация кредита в системе Colvir по программе Даму',
  COLVIR_CHECK_IF_CORP_CARD_ALREADY_CREATED = 'COLVIR_CHECK_IF_CORP_CARD_ALREADY_CREATED', // 'Проверка наличия открытой корпаративной карты',
  COLVIR_CREATE_CORP_CARD = 'COLVIR_CREATE_CORP_CARD', // 'Создание корпаративной карты',
  GENERATE_OPENING_ACCOUNT_DOCUMENTS = 'GENERATE_OPENING_ACCOUNT_DOCUMENTS', // 'Генерация документов об открытии счета в Colvir',
  GET_CORP_CARD_DATA = 'GET_CORP_CARD_DATA', // 'Получение данных по корпаративной карте',
  REGISTER_INSURANCE_IN_COLVIR = 'REGISTER_INSURANCE_IN_COLVIR', // 'Регистрация страхования в системе Colvir'
  OPEN_FL_CARD = 'OPEN_FL_CARD', // 'Открытие карточки клиента ФИЗ лица в системе Colvir'
  OPEN_PLEDGER_FL_CARD = 'OPEN_PLEDGER_FL_CARD', // 'Открытие карточки залогодателя ФИЗ лица в системе Colvir'
  OPEN_UL_CARD = 'OPEN_UL_CARD', // 'Открытие карточки клиента ЮР лица в системе Colvir'
  OPEN_BORROWER_ACCOUNT = 'OPEN_BORROWER_ACCOUNT', // 'Открытие счета в системе Colvir'
  GENERATE_MAIN_DOCUMENTS = 'GENERATE_MAIN_DOCUMENTS', // 'Генерация основных документов'
  GET_DOCS = 'GET_DOCS', // 'Получение дополнительных документов от заёмщика'
  PARSE_ADD_DOCS = 'PARSE_ADD_DOCS', // 'Парсинг и сохранение дополнительных документов'
  DOCUMENTS_VALIDATION = 'DOCUMENTS_VALIDATION', // 'Валидация документов через NovaFront'
  SPOUSE_AGREEMENT = 'SPOUSE_AGREEMENT', // 'Получение согласия супруги (При наличии)'
  GENERATE_DOCUMENTS_SIGNED_BY_ECP = 'GENERATE_DOCUMENTS_SIGNED_BY_ECP', // 'Генерация документов, подписанных ЭЦП'
  FINAL_SIGN = 'FINAL_SIGN', // 'Финальное подписание'
  DAMU_CREATE_APPLICATION = 'DAMU_CREATE_APPLICATION', // 'Создание заявки ДАМУ'
  DAMU_OBLIGATION_CHECK = 'DAMU_OBLIGATION_CHECK', // 'Проверка выпуска гарантии ДАМУ'
  DAMU_UPDATE_APPLICATION = 'DAMU_UPDATE_APPLICATION', // 'Обновление статуса заявки на стороне ДАМУ'
  GENERATE_REPAYMENT_SCHEDULE = 'GENERATE_REPAYMENT_SCHEDULE', // 'Формирование графика погашения'
  LOAN_ACTUALIZE = 'LOAN_ACTUALIZE', // 'Актуализация кредита'
  CREATE_MORTGAGE = 'CREATE_MORTGAGE', // 'Создание залога'
  CREATE_PROPERTY_MORTGAGE = 'CREATE_PROPERTY_MORTGAGE', // 'Создание залога недвижимости'
  CREATE_APPLICATION_IN_CRED_ADMIN = 'CREATE_APPLICATION_IN_CRED_ADMIN', // 'Создание кредитной заявки в сервисе Кредитного Администратора',
  SEND_LOAN_DATA_TO_CRED_ADMIN = 'SEND_LOAN_DATA_TO_CRED_ADMIN', // 'Отправка данных по кредиту в кредитной заявке в сервис Кредитного Администратора',
  SEND_BANK_DATA_TO_CRED_ADMIN = 'SEND_BANK_DATA_TO_CRED_ADMIN', // 'Отправка банковских данных в кредитной заявке в сервис Кредитного Администратора',
  SEND_ADDITIONAL_DOCUMENTS_TO_CRED_ADMIN = 'SEND_ADDITIONAL_DOCUMENTS_TO_CRED_ADMIN', // 'Отправка второй пачки документов в сервис Кредитного Администратора',
  SEND_BANK_REPAYMENT_SCHEDULE_SIGNATURE_TO_CRED_ADMIN = 'SEND_BANK_REPAYMENT_SCHEDULE_SIGNATURE_TO_CRED_ADMIN', // 'Отправка подписи банка для графика погашения в сервис Кредитного Администратора',
  SEND_BANK_LOAN_AGREEMENTS_BANK_SIGNATURE_TO_CRED_ADMIN = 'SEND_BANK_LOAN_AGREEMENTS_BANK_SIGNATURE_TO_CRED_ADMIN', // 'Отправка подписи банка для ДБЗ в сервис Кредитного Администратора',
  SEND_PLEDGE_AGREEMENTS_BANK_SIGNATURE_TO_CRED_ADMIN = 'SEND_PLEDGE_AGREEMENTS_BANK_SIGNATURE_TO_CRED_ADMIN', // 'Отправка подписи банка для ДЗ в сервис Кредитного Администратора'
  SEND_MAIN_DOCUMENTS_CLIENTS_SIGNATURE_TO_CRED_ADMIN = 'SEND_MAIN_DOCUMENTS_CLIENTS_SIGNATURE_TO_CRED_ADMIN', // 'Отправка подписи клиента для основных документов в сервис Кредитного Администратора',
  SEND_REPAYMENT_SCHEDULES_CLIENT_SIGNATURE_TO_CRED_ADMIN = 'SEND_REPAYMENT_SCHEDULES_CLIENT_SIGNATURE_TO_CRED_ADMIN', // 'Отправка подписи клиента для графика погашения в сервис Кредитного Администратора',
  SEND_PLEDGE_AGREEMENTS_CLIENT_SIGNATURE_TO_CRED_ADMIN = 'SEND_PLEDGE_AGREEMENTS_CLIENT_SIGNATURE_TO_CRED_ADMIN', // 'Отправка подписи клиента для ДЗ в сервис Кредитного Администратора'
  WAIT_FOR_CONFIRMATION_FROM_CRED_ADMIN = 'WAIT_FOR_CONFIRMATION_FROM_CRED_ADMIN', // 'Проверка заявки Кредитным Администратором',
  SECOND_CONFIRMATION_FROM_CRED_ADMIN = 'SECOND_CONFIRMATION_FROM_CRED_ADMIN', // 'Повторное подтверждение от Кредитного Администратора',
  UPDATE_APPLICATION_STATUS_COMPLETE_IN_CRED_ADMIN = 'UPDATE_APPLICATION_STATUS_COMPLETE_IN_CRED_ADMIN', // 'Обновление статуса заявки на завершено на стороне Кредитного Администратора',
  UPDATE_APPLICATION_STATUS_SECONDARY_CONFIRMATION_IN_CRED_ADMIN = 'UPDATE_APPLICATION_STATUS_SECONDARY_CONFIRMATION_IN_CRED_ADMIN', // 'Обновление статуса заявки на повторное подтверждение на стороне Кредитного Администратора',
  CREATE_APPLICATION_IN_TELEMARKETING = 'CREATE_APPLICATION_IN_TELEMARKETING', // 'Создание кредитной заявки в сервисе Телемаркетинга',
  UPDATE_LOAN_DATA_IN_TELEMARKETING = 'UPDATE_LOAN_DATA_IN_TELEMARKETING', // 'Обновление данных по кредиту в кредитной заявке сервиса Телемаркетинг',
  UPDATE_APPLICATION_STATUS_COMPLETE_IN_TELEMARKETING = 'UPDATE_APPLICATION_STATUS_COMPLETE_IN_TELEMARKETING', // 'Обновление статуса заявки на "завершено" в сервисе Телемаркетинга',
  UPDATE_APPLICATION_STATUS_NEW_IN_TELEMARKETING = 'UPDATE_APPLICATION_STATUS_NEW_IN_TELEMARKETING', // 'Обновление статуса заявки на "новый" в сервисе Телемаркетинга',
  UPDATE_APPLICATION_STATUS_REJECTED_IN_TELEMARKETING = 'UPDATE_APPLICATION_STATUS_REJECTED_IN_TELEMARKETING', // 'Обновление статуса заявки на "отклонено" в сервисе Телемаркетинга',
  UPDATE_APPLICATION_STATUS_SECONDARY_CONFIRMATION_IN_TELEMARKETING = 'UPDATE_APPLICATION_STATUS_SECONDARY_CONFIRMATION_IN_TELEMARKETING', // 'Обновление статуса заявки на "повторное подтверждение" в сервисе Телемаркетинга',
  UPLOAD_BANK_DATA_IN_TELEMARKETING = 'UPLOAD_BANK_DATA_IN_TELEMARKETING', // 'Обновление банковских данных в сервисе Телемаркетинга',
  UPLOAD_UL_DATA_IN_TELEMARKETING = 'UPLOAD_UL_DATA_IN_TELEMARKETING', // 'Обновление ЮЛ данных в сервисе Телемаркетинга',
  CHECK_BY_COLLATERAL = 'CHECK_BY_COLLATERAL', // 'Проверка залога залоговиком',
  LOGGY_CREATE_ORDER = 'LOGGY_CREATE_ORDER', // 'Создание заявки на доставку в Loggy',
  CHECK_ELICENSE = 'CHECK_ELICENSE', // 'Проверка талона Elicense',
  CHECK_ACCOUNT_BALANCE = 'CHECK_ACCOUNT_BALANCE', // 'Проверка баланса'
  CREATE_DEPOSIT = 'CREATE_DEPOSIT', // 'Создание депозита'
  FETCH_JUR_INFO = 'FETCH_JUR_INFO', // "Полчуние данных по Ю.Л"
  CREDIT_ADD_IN_CABINET = 'CREDIT_ADD_IN_CABINET', // 'Создание кредитной заявки в ЛК'
  FETCH_SPOUSE_PERSONAL_INFO = 'FETCH_SPOUSE_PERSONAL_INFO', // 'Процесс получения данных cупруга/и Залогодателя'
  SEND_SMS_AND_END_STEP = 'SEND_SMS_AND_END_STEP', // 'Отправка смс со ссылкой на главную страницу и завершение степа'
  PROPERTY_PLEDGES = 'PROPERTY_PLEDGES', // "Получение имущества для залога",
  APPROVE_PROPERTY_PLEDGES = 'APPROVE_PROPERTY_PLEDGES', // "Подтверждение имущества для залога",
  CHECKING_COLLATERAL = 'CHECKING_COLLATERAL', // "Проверка залога на обременение"
  CREATE_ESTIMATION = 'CREATE_ESTIMATION', // "Cоздание оценки"
  CHECKING_DOCUMENTS = 'CHECKING_DOCUMENTS', // "Проверка документов по тиупу на наличие"
  DECISION_COLLATERAL = 'DECISION_COLLATERAL', // "Решение залоговика"
  CLOSE_CURRENT_FLOW_NEXT_FLOW = 'CLOSE_CURRENT_FLOW_NEXT_FLOW', // "Создание след.флоу"
  DOCUMENT_SIGNING_AND_VERIFICATION = 'DOCUMENT_SIGNING_AND_VERIFICATION', // "Подписание документов  и проверка подписи"
  BLOCKCHAIN_CREATE_RECORD = 'BLOCKCHAIN_CREATE_RECORD', // "Создание записи в blockchain"
  SIGNING_ESTIMATOR = 'SIGNING_ESTIMATOR', // "Подписание оценщиком"
  COLVIR_MORTGAGE = 'COLVIR_MORTGAGE', // "Создание залога в колвире"
  NOTIFY_IN_TG_CHAT = 'NOTIFY_IN_TG_CHAT', // "Отправка в телеграмм чат"
  ESTIMATION_SERVICE_PAY = 'ESTIMATION_SERVICE_PAY', // "Оплату за услуги оценки"
  DECISION_ESTIMATOR = 'DECISION_ESTIMATOR', // "Решение оценщика"
  GENERATE_CONCLUSION_ON_COLLATERAL = 'GENERATE_CONCLUSION_ON_COLLATERAL', // 'Генерация заключения по залоговому обеспечению'
  DECISION_LAWYER = 'DECISION_LAWYER', // "Решение юриста"
  DECISION_NOTARY = 'DECISION_NOTARY', // "Решение нотариса"
  SET_ACCOUNT_LIMIT = 'SET_ACCOUNT_LIMIT', // "Установление неснижаемого остатка"
  MORTGAGE_REGISTER = 'MORTGAGE_REGISTER', // "Регистрация золога в колвир"
  CHOOSING_NOTARY = 'CHOOSING_NOTARY', // "Выбор нотариуса"
  AWAITING_CREDIT_MANAGER_DECISION = 'AWAITING_CREDIT_MANAGER_DECISION', // "Ожидание решение кредитного менеджера"
  AWAITING_THIRD_PARTY_PLEDGE_CONFIRMATION = 'AWAITING_THIRD_PARTY_PLEDGE_CONFIRMATION', // "Ожидание подтверждение залога третьего лица"
  AWAITING_THIRD_PARTY_CONDITIONS_CONFIRMATION = 'AWAITING_THIRD_PARTY_CONDITIONS_CONFIRMATION', // "Ожидание подтверждения условий залогодателем"
  THIRD_PARTY_PLEDGER_SCORING = 'THIRD_PARTY_PLEDGER_SCORING', // 'Скоринг залогодателя'
  THIRD_PARTY_PLEDGER_PRE_SCORE = 'THIRD_PARTY_PLEDGER_PRE_SCORE', // 'Процесс предварительного скоринга залогодателя'
  THIRD_PARTY_PLEDGER_BIOMETRY_FORENSICS = 'THIRD_PARTY_PLEDGER_BIOMETRY_FORENSICS', // Процесс биометрии залогодателя (forensics)
  AWAITING_THIRD_PARTY_PROPERTY_CHOICE = 'AWAITING_THIRD_PARTY_PROPERTY_CHOICE', // 'Ожидание выбора имущества третьего лица'
  AWAITING_THIRD_PARTY_PLEDGE_DECISION = 'AWAITING_THIRD_PARTY_PLEDGE_DECISION', // 'Ожидание решения клиента'
  FETCH_THIRD_PARTY_PLEDGER_DDOCS = 'FETCH_THIRD_PARTY_PLEDGER_DDOCS', // 'Получение УДЛ третьего лица из сервиса DigitalDocuments'
  THIRD_PARTY_PROPERTY_PLEDGES = 'THIRD_PARTY_PROPERTY_PLEDGES', // "Получение имущества третьего лица для залога"
  WAIT_FOR_SIGNATURES_VALIDATION_FROM_CRED_ADMIN = 'WAIT_FOR_SIGNATURES_VALIDATION_FROM_CRED_ADMIN', // Проверка статуса валидации подписей К.А.
  THIRD_PARTY_E_NOTARY = 'THIRD_PARTY_E_NOTARY', // 'Прохождение залогодателя e-нотариата'
  SEND_OTP_SMS = 'SEND_OTP_SMS', // Отправка OTP заемщику
  SIGN_DOCS_CERTIFICATION_CENTER = 'SIGN_DOCS_CERTIFICATION_CENTER', // Процесс подписания документов через УЦ
  SEND_THIRD_PARTY_PLEDGER_OTP_SMS = 'SEND_THIRD_PARTY_PLEDGER_OTP_SMS', // Отправка OTP залогодателю
  SIGN_DOCS_CERTIFICATION_CENTER_THIRD_PARTY_PLEDGER = 'SIGN_DOCS_CERTIFICATION_CENTER_THIRD_PARTY_PLEDGER', // Процесс подписания документов залогодателем через УЦ
  Q19_CALL = 'Q19_CALL', // 'Видеозвонок телемаркетинга'
  CHECK_DAILY_LIMIT = 'CHECK_DAILY_LIMIT', // Проверка лимитов
  OPERATOR_BMG = 'OPERATOR_BMG', // Проверка операторов и BMG
  FETCH_OWN_PROGRAM_SPOUSE_PERSONAL_INFO = 'FETCH_OWN_PROGRAM_SPOUSE_PERSONAL_INFO', // 'Процесс получения данных cупруга/и Залогодателя для собственной программы'
  FETCH_SPOUSE_PERSONAL_INFO_FOR_CONSENT = 'FETCH_SPOUSE_PERSONAL_INFO_FOR_CONSENT', // 'Процесс получения данных cупруга/и Залогодателя для собственной программы'
  SIGN_SPOUSE_DOCS_AITU = 'SIGN_SPOUSE_DOCS_AITU', // 'Процесс подписания документов DID(aitu) супруги'
  SEND_SPOUSE_SIGNING_SMS = 'SEND_SPOUSE_SIGNING_SMS', // 'Отправка смс супруге для подписания'
  SIGN_PLEDGE_DOCS_CERTIFICATION_CENTER = 'SIGN_PLEDGE_DOCS_CERTIFICATION_CENTER', // Процесс подписания документов через УЦ для залоговой программы
  ACQUAINTANCE_WITH_DOCUMENTS = 'ACQUAINTANCE_WITH_DOCUMENTS', // Процесс Ознакомление заемщика с документами
  ACQUAINTANCE_THIRD_PARTY_PLEDGER_WITH_DOCUMENTS = 'ACQUAINTANCE_THIRD_PARTY_PLEDGER_WITH_DOCUMENTS', // Процесс Ознакомление залогодателя с документами
  ACQUAINTANCE_SPOUSE_WITH_DOCUMENTS = 'ACQUAINTANCE_SPOUSE_WITH_DOCUMENTS', // Процесс Ознакомление супруга с документами
  SPOUSE_BIOMETRY_FORENSICS = 'SPOUSE_BIOMETRY_FORENSICS', // Процесс биометрии супруги (forensics)
  SEND_SPOUSE_OTP_SMS = 'SEND_SPOUSE_OTP_SMS', // Отправка OTP супруге
  SIGN_SPOUSE_DOCS_CERTIFICATION_CENTER = 'SIGN_SPOUSE_DOCS_CERTIFICATION_CENTER', // Процесс подписания документов через УЦ супруги
  SIGN_OWN_DOCS_CERTIFICATION_CENTER = 'SIGN_OWN_DOCS_CERTIFICATION_CENTER', // Процесс подписания документов через УЦ для собственной программы
  AWAITING_GUARANTOR_INDIVIDUAL_CONFIRMATION = 'AWAITING_GUARANTOR_INDIVIDUAL_CONFIRMATION', // Ожидание подтверждение гаранта
  FETCH_GUARANTOR_INDIVIDUAL_PERSONAL_INFO = 'FETCH_GUARANTOR_INDIVIDUAL_PERSONAL_INFO', // Процесс получения данных гаранта
  GENERATE_GUARANTOR_INDIVIDUAL_DOCUMENTS_SIGNED_BY_OTP = 'GENERATE_GUARANTOR_INDIVIDUAL_DOCUMENTS_SIGNED_BY_OTP', // Генерация документов гаранта, подписанных OTP
  GUARANTOR_INDIVIDUAL_PRE_SCORE = 'GUARANTOR_INDIVIDUAL_PRE_SCORE', // Процесс предварительного скоринга гаранта
  GUARANTOR_INDIVIDUAL_BIOMETRY_FORENSICS = 'GUARANTOR_INDIVIDUAL_BIOMETRY_FORENSICS', // Процесс биометрии гаранта (forensics)
  GUARANTOR_INDIVIDUAL_SCORING = 'GUARANTOR_INDIVIDUAL_SCORING', // Скоринг гаранта
  FETCH_GUARANTOR_INDIVIDUAL_PKB_REPORT = 'FETCH_GUARANTOR_INDIVIDUAL_PKB_REPORT', // Получение кредитного отчета ПКБ - гарант
  AWAITING_GUARANTOR_CONDITIONS_CONFIRMATION = 'AWAITING_GUARANTOR_CONDITIONS_CONFIRMATION', // Ожидание подтверждения условий гарантом
  FETCH_GUARANTOR_INDIVIDUAL_DDOCS = 'FETCH_GUARANTOR_INDIVIDUAL_DDOCS', // Получение УДЛ гаранта из сервиса DigitalDocuments
  OPEN_GUARANTOR_CARD = 'OPEN_GUARANTOR_CARD', // Открытие карточки гаранта ФИЗ лица в системе Colvir
  ACQUAINTANCE_GUARANTOR_WITH_DOCUMENTS = 'ACQUAINTANCE_GUARANTOR_WITH_DOCUMENTS', // Ознакомление гаранта с документами
  SEND_GUARANTOR_INDIVIDUAL_OTP_SMS = 'SEND_GUARANTOR_INDIVIDUAL_OTP_SMS', // Отправка OTP гаранта
  SIGN_DOCS_CERTIFICATION_CENTER_GUARANTOR_INDIVIDUAL = 'SIGN_DOCS_CERTIFICATION_CENTER_GUARANTOR_INDIVIDUAL', // Процесс подписания документов гаранта через УЦ
  SIGN_GUARANT_AGREEMENT_BY_BANK = 'SIGN_GUARANT_AGREEMENT_BY_BANK', // Процесс подписания ДГ банком
  SIGN_BIOMETRY_FORENSICS = 'SIGN_BIOMETRY_FORENSICS' //
}
